import { defineComponent } from 'vue';
import { createRouter, createMemoryHistory, createWebHistory } from 'vue-router';
import * as c from '@model/const/sites';

// Fooling rollup here, so it doesn't statically link all containers to the app chunk.
// For SSR, eagerly import all containers, for client make it lazy
let modules = null;
function getModules() {
    if (modules) {
        return modules;
    }

    modules = import.meta.glob('/src/vue3/views/**/container.vue', { eager: import.meta.env.SSR });

    return modules;
}

// Don't re-wrap the same module on every request
const wrappedModulesCache = new Map<string, any>();

const viewFactory = async (siteID, view) => {
    const path = `/src/vue3/views/${siteID}/${view}/container.vue`;

    if (wrappedModulesCache.has(path)) {
        return wrappedModulesCache.get(path);
    }

    const mod = getModules()[path];

    if (!mod) {
        throw new Error(`Component not found: ${path}`);
    }

    // For client, just return as is, no wrapping or caching required
    if (!import.meta.env.SSR) {
        return await mod();
    }

    // For SSR we need to wrap the default export to intercept the serverPrefetch
    const original = mod.default;

    const wrapped = defineComponent({
        ...original,
        name: `${original.name}-interruptible`,
        async serverPrefetch() {
            if (!original.serverPrefetch) {
                return;
            }

            try {
                await original.serverPrefetch.call(this);
            } catch (err) {
                this.$ssrContext.rejection = err;
            }
        },
        render() {
            if (typeof window === 'undefined' && this.$ssrContext?.rejection) {
                throw this.$ssrContext.rejection;
            }

            return original.render.call(this);
        },
    });

    wrappedModulesCache.set(path, wrapped);

    return wrapped;
};

const getRoutes = (siteID) => {
    const routes: Record<string, any[]> = {
        [c.SITE_ID_WWW]: [
            // Inapp
            {
                name: 'inapp',
                path: '/:locale/inapp/:pathMatch(.*)*/',
                component: () => viewFactory(c.SITE_ID_WWW, 'inapp/pages'),
                pathToRegexpOptions: { strict: true },
            },
            // Blog
            {
                name: 'blog-home',
                path: '/:lang/blog/',
                component: () => viewFactory(c.SITE_ID_WWW, 'blog/home'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-home-page',
                path: '/:lang/blog/page/:page/',
                component: () => viewFactory(c.SITE_ID_WWW, 'blog/home'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-post-draft',
                path: '/:lang/blog/posts/:slug/draft-:draftKey/',
                component: () => viewFactory(c.SITE_ID_WWW, 'blog/post'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-post',
                path: '/:lang/blog/posts/:slug/',
                component: () => viewFactory(c.SITE_ID_WWW, 'blog/post'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-category',
                path: '/:lang/blog/categories/:category/',
                component: () => viewFactory(c.SITE_ID_WWW, 'blog/category'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-category-page',
                path: '/:lang/blog/categories/:category/page/:page/',
                component: () => viewFactory(c.SITE_ID_WWW, 'blog/category'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-tag',
                path: '/:lang/blog/tags/:tag/',
                component: () => viewFactory(c.SITE_ID_WWW, 'blog/tag'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-tag-page',
                path: '/:lang/blog/tags/:tag/page/:page/',
                component: () => viewFactory(c.SITE_ID_WWW, 'blog/tag'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-authors',
                path: '/:lang/blog/authors/',
                component: () => viewFactory(c.SITE_ID_WWW, 'blog/authors'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-author-posts',
                path: '/:lang/blog/authors/:author/',
                component: () => viewFactory(c.SITE_ID_WWW, 'blog/author-posts'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-author-posts-page',
                path: '/:lang/blog/authors/:author/page/:page/',
                component: () => viewFactory(c.SITE_ID_WWW, 'blog/author-posts'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-search',
                path: '/:lang/blog/search/',
                component: () => viewFactory(c.SITE_ID_WWW, 'blog/search'),
                pathToRegexpOptions: { strict: true },
            },
            // Blog CPC (TRU Security)
            {
                name: 'blog-cpc-home',
                path: '/:lang/tru/',
                component: () => viewFactory(c.SITE_ID_WWW, 'cpc/home'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-cpc-about',
                path: '/:lang/tru/about/',
                component: () => viewFactory(c.SITE_ID_WWW, 'cpc/about'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-cpc-home-page',
                path: '/:lang/tru/page/:page/',
                component: () => viewFactory(c.SITE_ID_WWW, 'cpc/home'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-cpc-news',
                path: '/:lang/tru/posts/',
                component: () => viewFactory(c.SITE_ID_WWW, 'cpc/news'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-cpc-news-page',
                path: '/:lang/tru/posts/page/:page/',
                component: () => viewFactory(c.SITE_ID_WWW, 'cpc/news'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-cpc-post-draft',
                path: '/:lang/tru/posts/:slug/draft-:draftKey/',
                component: () => viewFactory(c.SITE_ID_WWW, 'cpc/post'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-cpc-post',
                path: '/:lang/tru/posts/:slug/',
                component: () => viewFactory(c.SITE_ID_WWW, 'cpc/post'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-cpc-author-posts',
                path: '/:lang/tru/authors/:author/',
                component: () => viewFactory(c.SITE_ID_WWW, 'cpc/author-posts'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-cpc-author-posts-page',
                path: '/:lang/tru/authors/:author/page/:page/',
                component: () => viewFactory(c.SITE_ID_WWW, 'cpc/author-posts'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-cpc-category',
                path: '/:lang/tru/categories/:slug/',
                component: () => viewFactory(c.SITE_ID_WWW, 'cpc/category'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-cpc-category-page',
                path: '/:lang/tru/categories/:slug/page/:page/',
                component: () => viewFactory(c.SITE_ID_WWW, 'cpc/category'),
                pathToRegexpOptions: { strict: true },
            },
            // TRU Threat Catalog
            {
                name: 'blog-threat-catalog-home',
                path: '/:lang/tru/threat-catalog/',
                component: () => viewFactory(c.SITE_ID_WWW, 'threat-catalog/home'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-threat-catalog-post',
                path: '/:lang/tru/threat-catalog/:slug+/',
                component: () => viewFactory(c.SITE_ID_WWW, 'threat-catalog/post'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-threat-catch-normalized-schema',
                path: '/:lang/threat/posts/:slug(.*)',
                component: () => viewFactory(c.SITE_ID_WWW, 'threat-catalog/catch-normalized-schema'),
                pathToRegexpOptions: { strict: true },
            },
            // Careers Job page
            {
                name: 'careers-job',
                path: '/:lang/careers/job/:id?/',
                component: () => viewFactory(c.SITE_ID_WWW, 'careers-job'),
                pathToRegexpOptions: { strict: true },
            },
            // PR
            {
                name: 'pr-home-page',
                path: '/:lang/pr/',
                component: () => viewFactory(c.SITE_ID_WWW, 'pr-home-page'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'pr-home-page-page',
                path: '/:lang/pr/page/:page/',
                component: () => viewFactory(c.SITE_ID_WWW, 'pr-home-page'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'pr-media-contacts',
                path: '/:lang/pr/media-sources/',
                component: () => viewFactory(c.SITE_ID_WWW, 'pr-media-contacts'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'pr-by-year',
                path: '/:lang/pr/:year/',
                component: () => viewFactory(c.SITE_ID_WWW, 'pr-by-year'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'pr-by-year-page',
                path: '/:lang/pr/:year/page/:page/',
                component: () => viewFactory(c.SITE_ID_WWW, 'pr-by-year'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'pr-press-release-draft',
                path: '/:lang/pr/:year(\\d+)/:slug/draft-:draftKey/',
                component: () => viewFactory(c.SITE_ID_WWW, 'pr-press-release'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'pr-press-release',
                path: '/:lang/pr/:year(\\d+)/:slug/',
                component: () => viewFactory(c.SITE_ID_WWW, 'pr-press-release'),
                pathToRegexpOptions: { strict: true },
            },
            // RC
            {
                name: 'resource-center-home',
                path: '/:lang/resource-center/',
                component: () => viewFactory(c.SITE_ID_WWW, 'resource-center-home'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'resource-center-category',
                path: '/:lang/resource-center/category/:slug/',
                component: () => viewFactory(c.SITE_ID_WWW, 'resource-center-category'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'resource-center-category-page',
                path: '/:lang/resource-center/category/:slug/page/:page/',
                component: () => viewFactory(c.SITE_ID_WWW, 'resource-center-category'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'resource-center-audience',
                path: '/:lang/resource-center/audience/:slug/',
                component: () => viewFactory(c.SITE_ID_WWW, 'resource-center-audience'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'resource-center-audience-page',
                path: '/:lang/resource-center/audience/:slug/page/:page/',
                component: () => viewFactory(c.SITE_ID_WWW, 'resource-center-audience'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'resource-center-tag',
                path: '/:lang/resource-center/tag/:slug/',
                component: () => viewFactory(c.SITE_ID_WWW, 'resource-center-tag'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'resource-center-tag-page',
                path: '/:lang/resource-center/tag/:slug/page/:page/',
                component: () => viewFactory(c.SITE_ID_WWW, 'resource-center-tag'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'resource-center-resource-draft',
                path: '/:lang/resource-center/resource/:slug/draft-:draftKey/',
                component: () => viewFactory(c.SITE_ID_WWW, 'resource-center-resource'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'resource-center-resource',
                path: '/:lang/resource-center/resource/:slug/',
                component: () => viewFactory(c.SITE_ID_WWW, 'resource-center-resource'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'resource-center-search',
                path: '/:lang/resource-center/search/',
                component: () => viewFactory(c.SITE_ID_WWW, 'resource-center-search'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'resource-center-search-page',
                path: '/:lang/resource-center/search/page/:page/',
                component: () => viewFactory(c.SITE_ID_WWW, 'resource-center-search'),
                pathToRegexpOptions: { strict: true },
            },
            // Solutions / Integrations (moved from solutions.acronis.com)
            {
                path: '/:locale/integrations/',
                name: 'solutions-home',
                component: () => viewFactory(c.SITE_ID_WWW, 'solutions/home'),
                pathToRegexpOptions: { strict: true },
            },
            {
                path: '/:locale/integrations/catalog/',
                name: 'solutions-catalog',
                component: () => viewFactory(c.SITE_ID_WWW, 'solutions/catalog'),
                pathToRegexpOptions: { strict: true },
            },
            {
                path: '/:locale/integrations/catalog/page/:page/',
                name: 'solutions-catalog-page',
                component: () => viewFactory(c.SITE_ID_WWW, 'solutions/catalog'),
                pathToRegexpOptions: { strict: true },
            },
            {
                path: '/:locale/integrations/catalog/category/:category/:subcategory?/',
                name: 'solutions-catalog-category',
                component: () => viewFactory(c.SITE_ID_WWW, 'solutions/catalog'),
                pathToRegexpOptions: { strict: true },
            },
            {
                path: '/:locale/integrations/catalog/category/:category/page/:page/',
                name: 'solutions-catalog-category-page',
                component: () => viewFactory(c.SITE_ID_WWW, 'solutions/catalog'),
                pathToRegexpOptions: { strict: true },
            },
            {
                path: '/:locale/integrations/catalog/category/:category/:subcategory/page/:page/',
                name: 'solutions-catalog-subcategory-page',
                component: () => viewFactory(c.SITE_ID_WWW, 'solutions/catalog'),
                pathToRegexpOptions: { strict: true },
            },
            {
                path: '/:locale/integrations/:slug/',
                name: 'solutions-integration',
                component: () => viewFactory(c.SITE_ID_WWW, 'solutions/integration'),
                pathToRegexpOptions: { strict: true },
            },
            // Errors
            {
                name: 'http-error-404',
                path: '/:lang/http-errors/404/',
                component: () => viewFactory(c.SITE_ID_WWW, 'http-error-404'),
                pathToRegexpOptions: { strict: true },
            },
            // Pages && catch-all
            {
                name: 'pages-preview',
                path: '/:lang/preview/:previewId/',
                component: () => viewFactory(c.SITE_ID_WWW, 'pages'),
            },
            {
                name: 'pages-page',
                path: '/:lang/:pathMatch(.*)*/',
                component: () => viewFactory(c.SITE_ID_WWW, 'pages'),
            },
        ],
        [c.SITE_ID_TRUEIMAGE]: [
            // NOTES:
            // - TI site is basically cloned from SITE_ID_WWW, but with a small subset of views only – this is the scope of Phase 1.
            // - While these views are (almost) identical, it's not recommended to reuse original ones, because more changes are coming.
            // Blog
            {
                name: 'blog-home',
                path: '/:locale/blog/',
                component: () => viewFactory(c.SITE_ID_TRUEIMAGE, 'blog/home'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-home-page',
                path: '/:locale/blog/page/:page/',
                component: () => viewFactory(c.SITE_ID_TRUEIMAGE, 'blog/home'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-post-draft',
                path: '/:locale/blog/posts/:slug/draft-:draftKey/',
                component: () => viewFactory(c.SITE_ID_TRUEIMAGE, 'blog/post'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-post',
                path: '/:locale/blog/posts/:slug/',
                component: () => viewFactory(c.SITE_ID_TRUEIMAGE, 'blog/post'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-category',
                path: '/:locale/blog/categories/:category/',
                component: () => viewFactory(c.SITE_ID_TRUEIMAGE, 'blog/category'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-category-page',
                path: '/:locale/blog/categories/:category/page/:page/',
                component: () => viewFactory(c.SITE_ID_TRUEIMAGE, 'blog/category'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-tag',
                path: '/:locale/blog/tags/:tag/',
                component: () => viewFactory(c.SITE_ID_TRUEIMAGE, 'blog/tag'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-tag-page',
                path: '/:locale/blog/tags/:tag/page/:page/',
                component: () => viewFactory(c.SITE_ID_TRUEIMAGE, 'blog/tag'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'blog-search',
                path: '/:locale/blog/search/',
                component: () => viewFactory(c.SITE_ID_TRUEIMAGE, 'blog/search'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'http-error-404',
                path: '/:locale/http-errors/404/',
                component: () => viewFactory(c.SITE_ID_TRUEIMAGE, 'http-error-404'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'pages-preview',
                path: '/:locale/preview/:previewId/',
                component: () => viewFactory(c.SITE_ID_TRUEIMAGE, 'pages'),
            },
            {
                name: 'pages-page',
                path: '/:locale/:pathMatch(.*)*/',
                component: () => viewFactory(c.SITE_ID_TRUEIMAGE, 'pages'),
            },
        ],
        [c.SITE_ID_EVENTS]: [
            {
                name: 'archive',
                path: '/archive/',
                component: () => viewFactory(c.SITE_ID_EVENTS, 'archive'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'archive-localized',
                path: '/:locale/archive/',
                component: () => viewFactory(c.SITE_ID_EVENTS, 'archive'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'ot-events',
                path: '/category/ot-industrial-events/',
                component: () => viewFactory(c.SITE_ID_EVENTS, 'ot-events'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'ot-events-localized',
                path: '/:locale/category/ot-industrial-events/',
                component: () => viewFactory(c.SITE_ID_EVENTS, 'ot-events'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'category',
                path: '/category/:category/',
                component: () => viewFactory(c.SITE_ID_EVENTS, 'category'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'category-localized',
                path: '/:locale/category/:category/',
                component: () => viewFactory(c.SITE_ID_EVENTS, 'category'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'event',
                path: '/event/:slug/:key?/',
                component: () => viewFactory(c.SITE_ID_EVENTS, 'event'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'event-localized',
                path: '/:locale/event/:slug/:key?/',
                component: () => viewFactory(c.SITE_ID_EVENTS, 'event'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'http-error-404',
                path: '/http-errors/404/',
                component: () => viewFactory(c.SITE_ID_EVENTS, 'http-error-404'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'http-error-404-localized',
                path: '/:locale/http-errors/404/',
                component: () => viewFactory(c.SITE_ID_EVENTS, 'http-error-404'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'home',
                path: '/',
                component: () => viewFactory(c.SITE_ID_EVENTS, 'home'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'home-localized',
                path: '/:locale/',
                component: () => viewFactory(c.SITE_ID_EVENTS, 'home'),
                pathToRegexpOptions: { strict: true },
            },
        ],
        [c.SITE_ID_SCS]: [
            {
                name: 'http-error-404',
                path: '/http-errors/404/',
                component: () => viewFactory(c.SITE_ID_SCS, 'http-error-404'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'pages-preview',
                path: '/preview/:previewId/',
                component: () => viewFactory(c.SITE_ID_SCS, 'pages'),
            },
            {
                name: 'pages-page',
                path: '/:pathMatch(.*)*',
                component: () => viewFactory(c.SITE_ID_SCS, 'pages'),
            },
        ],
        [c.SITE_ID_DEVELOPER]: [
            {
                name: 'http-error-404',
                path: '/:locale/http-errors/404/',
                component: () => viewFactory(c.SITE_ID_DEVELOPER, 'http-error-404'),
                pathToRegexpOptions: { strict: true },
            },
            {
                name: 'pages-preview',
                path: '/:locale/preview/:previewId/',
                component: () => viewFactory(c.SITE_ID_DEVELOPER, 'pages'),
            },
            {
                name: 'pages-page',
                path: '/:locale/:pathMatch(.*)*/',
                component: () => viewFactory(c.SITE_ID_DEVELOPER, 'pages'),
            },
        ],
        [c.SITE_ID_ACADEMY]: [
            {
                name: 'http-error-404',
                path: '/:locale/http-errors/404/',
                component: () => viewFactory(c.SITE_ID_ACADEMY, 'http-error-404'),
                pathToRegexpOptions: { strict: true },
            },
            {
                path: '/:locale/',
                name: 'academy-home',
                component: () => viewFactory(c.SITE_ID_ACADEMY, 'home'),
            },
            {
                path: '/:locale/catalogue/',
                name: 'academy-catalogue',
                component: () => viewFactory(c.SITE_ID_ACADEMY, 'catalogue'),
            },
            {
                path: '/:locale/catalogue/:slug/',
                name: 'academy-catalogue-course',
                component: () => viewFactory(c.SITE_ID_ACADEMY, 'course'),
            },
        ],
        [c.SITE_ID_TOOLBOX]: [
            {
                name: 'builder-page',
                path: '/slices-builder/',
                component: () => viewFactory(c.SITE_ID_TOOLBOX, 'builder'),
                pathToRegexOptions: { string: true },
            },
        ],
    };

    return routes[siteID] || [];
};

export function RouterFactory(context, scene) {
    let currentRoutes = getRoutes(context.public.siteID);

    // Hack routes list to leave only page 404 alive. See @model/ssr/context.ts for explanation
    if (context.public.isNotFoundTrap) {
        const route404 = currentRoutes.find((r) => r.name === 'http-error-404');

        let fixedPath = '/:pathMatch(.*)*';

        if (currentRoutes.some((r) => r.path.startsWith('/:locale/'))) {
            fixedPath = '/:locale/:pathMatch(.*)*';
        }
        if (currentRoutes.some((r) => r.path.startsWith('/:lang/'))) {
            fixedPath = '/:lang/:pathMatch(.*)*';
        }

        const trap = {
            ...route404,
            path: fixedPath,
            pathToRegexpOptions: {},
        };

        currentRoutes = [trap];
    }

    const router = createRouter({
        history: scene === 'ssr' ? createMemoryHistory() : createWebHistory(),
        routes: currentRoutes,
    });

    return router;
}
